import { AuthBaseClient } from '../base-client';
import type { AddToWaitlistRequest, AddToWaitlistResponse } from '../appointment/types';
import type {
  ListWaitlistQuery,
  ListWaitlistResponse,
  RemoveFromWaitlistRequest,
  UpdateWaitlistRequest,
  WaitlistEntry,
  WaitlistEntryRefreshResponse,
  WaitlistRefreshResponse,
} from './types';

export class WaitlistClient extends AuthBaseClient {
  private basePath = '/api/waitlist';

  async list(query: ListWaitlistQuery = {}): Promise<ListWaitlistResponse> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    }
    const qs = params.toString();
    return this.get<ListWaitlistResponse>(`${this.basePath}${qs ? `?${qs}` : ''}`);
  }

  async getById(id: string): Promise<WaitlistEntry> {
    return this.get<WaitlistEntry>(`${this.basePath}/${id}`);
  }

  async add(request: AddToWaitlistRequest): Promise<AddToWaitlistResponse> {
    return this.post<AddToWaitlistResponse>(this.basePath, request);
  }

  async update(id: string, request: UpdateWaitlistRequest): Promise<WaitlistEntry> {
    return this.patch<WaitlistEntry>(`${this.basePath}/${id}`, request);
  }

  async remove(id: string, request: RemoveFromWaitlistRequest): Promise<WaitlistEntry> {
    return this.post<WaitlistEntry>(`${this.basePath}/${id}/remove`, request);
  }

  /**
   * Send the entry's queued change to the EHR now instead of waiting for the
   * scheduled push. Returns the entry as queued; the push itself runs in the
   * background, so poll the entry for its sync status.
   */
  async pushToEhr(id: string): Promise<WaitlistEntry> {
    return this.post<WaitlistEntry>(`${this.basePath}/${id}/push`, {});
  }

  /** Re-read one entry from the EHR now, and say what that found. */
  async refreshEntry(id: string): Promise<WaitlistEntryRefreshResponse> {
    return this.post<WaitlistEntryRefreshResponse>(`${this.basePath}/${id}/refresh`, {});
  }

  /** Read the EHR's whole list now instead of waiting for the next scheduled read. */
  async refresh(): Promise<WaitlistRefreshResponse> {
    return this.post<WaitlistRefreshResponse>(`${this.basePath}/refresh`, {});
  }
}
